import {
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  matchRoutes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { Box, Fade, LinearProgress, Typography } from "@mui/material";

import { useTheme } from "@emotion/react";
import SatelliteLoading from "assets/satellite-loading.gif"; // Placeholder for your loading graphic
import ButtonAtom from "components/ButtonAtom";
import usePageMeta from "hooks/usePageMeta";
import { useUser } from "provider/auth/useUser";
import analytics from "services/AnalyticsService";
import RoutePaths, { ACCESS, canUserAccessRoute, getRoute } from "./routes";

/**
 * Records a page view for every route the app shows, including in-app
 * navigation. The ref skips repeats of the same path (StrictMode re-runs
 * effects in development).
 */
function usePageViewTracking() {
  const { pathname } = useLocation();
  const tracked = useRef(null);
  useEffect(() => {
    if (tracked.current === pathname) return;
    tracked.current = pathname;
    analytics.trackPageView();
  }, [pathname]);
}

// The same paths <Routes> matches, so matchRoutes ranks them the same way
const MATCHABLE = RoutePaths.map((r) => ({ path: r.route, key: r.key }));

/**
 * Sets the title, description and index rule for the route on screen, from
 * its `seo` entry in routes.jsx. A route with `seo.fromPage` sets its own.
 */
function RouteMeta() {
  const { pathname } = useLocation();
  const key = matchRoutes(MATCHABLE, pathname)?.[0]?.route.key;
  const route = RoutePaths.find((r) => r.key === key);
  if (route?.seo?.fromPage) return null;
  return <RouteMetaTags route={route} pathname={pathname} />;
}

function RouteMetaTags({ route, pathname }) {
  usePageMeta({
    title: route?.seo?.title ?? route?.name,
    description: route?.seo?.description,
    path: pathname,
    noindex: !route?.seo,
  });
  return null;
}

/**
 * Enhanced Loading component for route transitions with dynamic animations
 * and better visual feedback for indefinite loading times
 */

const RouteLoader = memo(() => {
  const [elapsed, setElapsed] = useState(0);
  const [isLongLoad, setIsLongLoad] = useState(false);
  const theme = useTheme();

  // Loading states with improved copy
  const loadingStates = useMemo(
    () => [
      { time: 0, text: "Preparing your content" },
      { time: 2, text: "Loading content" },
      { time: 4, text: "Almost there" },
      { time: 7, text: "You might want to refresh" },
    ],
    [],
  );

  // Get current loading message based on elapsed time
  const currentLoadingState = useMemo(() => {
    const state = [...loadingStates]
      .reverse()
      .find((state) => elapsed >= state.time);
    return state ? state.text : loadingStates[0].text;
  }, [elapsed, loadingStates]);

  // Dots animation separated into its own state for better control
  const [loadingDots, setLoadingDots] = useState("");

  // Handle refresh
  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // Updated timers with cleanup
  useEffect(() => {
    // Dot animation
    const dotTimer = setInterval(() => {
      setLoadingDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400); // Slightly faster for better perceived performance

    // Elapsed time counter
    const elapsedTimer = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    // Set long load flag after threshold
    const longLoadTimer = setTimeout(() => {
      setIsLongLoad(true);
    }, 5000);

    // Cleanup all timers
    return () => {
      clearInterval(dotTimer);
      clearInterval(elapsedTimer);
      clearTimeout(longLoadTimer);
    };
  }, []);

  // Calculate progress for bounded progress indicator
  const boundedProgress = useMemo(() => {
    // Cap at 90% to maintain impression that something is still happening
    return Math.min(elapsed * 7, 90);
  }, [elapsed]);

  return (
    <Fade in={true} timeout={400}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          width: "100%",
          position: "fixed",
          top: 0,
          left: 0,
          backdropFilter: "blur(10px)",
          backgroundColor: `${theme.palette.background.default}BF`, // More opaque for better readability
          zIndex: 1000,
          padding: 3,
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Box
          sx={{
            width: "90%",
            maxWidth: "400px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Loading graphic with fade-in animation */}
          <Box
            component="img"
            loading="eager" // Prioritize loading
            src={SatelliteLoading}
            alt="Loading animation"
            sx={{
              width: "50%",
              maxWidth: "160px",
              height: "auto",
              objectFit: "cover",
              objectPosition: "center",
              borderRadius: "12px",
              animation: "pulse 2s infinite ease-in-out",
              "@keyframes pulse": {
                "0%": { opacity: 0.8 },
                "50%": { opacity: 1 },
                "100%": { opacity: 0.8 },
              },
            }}
          />

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              letterSpacing: 0.5,
              marginBottom: 1,
              color: theme.palette.text.primary,
              animation: "fadeIn 0.5s ease-in-out",
              "@keyframes fadeIn": {
                "0%": { opacity: 0 },
                "100%": { opacity: 1 },
              },
            }}
          >
            {currentLoadingState}
            <span className="loading-dots">{loadingDots}</span>
          </Typography>

          {/* Switch to determinate progress after a few seconds to show meaningful progress */}
          <LinearProgress
            variant={elapsed < 3 ? "indeterminate" : "determinate"}
            value={boundedProgress}
            sx={{
              width: "100%",
              height: 6,
              borderRadius: 3,
              marginBottom: 2,
              backgroundColor: theme.palette.grey[200],
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                transition: "transform 0.4s ease",
              },
            }}
          />

          {/* Message and refresh button for long loads */}
          {isLongLoad && (
            <Fade in={true}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  alignItems: "center",
                  // justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontWeight: 400,
                    marginBottom: 2,
                  }}
                >
                  This is taking longer than expected. Please try again.
                </Typography>

                <ButtonAtom
                  variant="outlined"
                  color="primary"
                  onClick={handleRefresh}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Refresh Page
                </ButtonAtom>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Fade>
  );
});

RouteLoader.displayName = "RouteLoader";

/**
 * Protected route wrapper component
 * Handles route access control based on user authentication and permissions
 */
const ProtectedRoute = memo(({ routeKey, userData, isPending, children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // PUBLIC routes (home, 404, faq, etc.) don't require user data to render.
  // Gating them on isPending would hide the page behind the loader whenever
  // the backend is slow or unreachable — including the 404 page itself.
  const routeAccess = useMemo(
    () => RoutePaths.find((r) => r.key === routeKey)?.access,
    [routeKey],
  );
  const isPublicRoute = routeAccess === ACCESS.PUBLIC;

  // Handle access control with useEffect instead of conditional rendering
  // This prevents the infinite redirect loop causing "Maximum update depth exceeded"
  useEffect(() => {
    if (isPublicRoute) return;
    // Don't run effect if still loading user data
    if (isPending) return;

    const hasAccess = canUserAccessRoute(userData, routeKey);
    const isHomeRoute = routeKey === "home";

    if (!hasAccess) {
      // If user is not authenticated, remember the intended destination
      // and send them to the login page. Use `username` as the "logged in"
      // test to stay consistent with the rest of the app — `userData` itself
      // can be a truthy-but-empty shape during error/loading states.
      if (!userData?.username) {
        localStorage.setItem("intendedRoute", location.pathname);
        navigate(getRoute("login"), { replace: true });
        return;
      }

      // Logged-in user landed on an UNAUTHENTICATED-only page (e.g. /login,
      // /signup). Prefer ?redirect=<path>, then the saved intendedRoute from
      // a previous bounce, then home.
      if (routeAccess === ACCESS.UNAUTHENTICATED) {
        const queryRedirect = new URLSearchParams(location.search).get(
          "redirect",
        );
        const intended = localStorage.getItem("intendedRoute");
        if (intended) localStorage.removeItem("intendedRoute");
        navigate(queryRedirect || intended || getRoute("home"), {
          replace: true,
        });
        return;
      }

      // Handle users with pending approval
      if (
        userData.status === "pending_approval" ||
        userData.status === "approved"
      ) {
        navigate(getRoute("pending_activation"), { replace: true });
        return;
      }

      // Authenticated user without permission for this page → home.
      if (!isHomeRoute) {
        navigate(getRoute("home"), { replace: true });
        return;
      }
    }
  }, [
    userData,
    isPending,
    routeKey,
    location.pathname,
    navigate,
    isPublicRoute,
  ]);

  // Public routes render immediately — they don't need user data.
  if (isPublicRoute) return children;

  // Show loader while authentication state is being determined
  if (isPending) {
    return <RouteLoader />;
  }

  // Render children directly without conditional Navigate components
  // This avoids rendering Navigate within the render phase which causes the loop
  return children;
});

ProtectedRoute.displayName = "ProtectedRoute";

/**
 * Routes component that must be inside the Router context
 * This is necessary to use hooks like useLocation
 */
const AppRoutes = memo(() => {
  // Move userQuery here so it's fetched once for all routes
  const userQuery = useUser();
  usePageViewTracking();

  return (
    <>
      <RouteMeta />
      <Routes>
        {RoutePaths.map((route) => (
          <Route
            key={route.key}
            path={route.route}
            element={
              <ProtectedRoute
                routeKey={route.key}
                userData={userQuery.data}
                isPending={userQuery.isPending}
              >
                {route.component}
              </ProtectedRoute>
            }
          />
        ))}

        {/* Fallback route for unmatched paths */}
        <Route
          path="*"
          element={<Navigate to={getRoute("error404")} replace />}
        />
      </Routes>
    </>
  );
});

AppRoutes.displayName = "AppRoutes";

/**
 * Main router component
 * Renders all application routes with proper access control
 */
const WebRoute = memo(() => {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteLoader />}>
        <AppRoutes />
      </Suspense>
    </BrowserRouter>
  );
});

WebRoute.displayName = "WebRoute";

export default WebRoute;
